/**
 * Wave C · levels (Vania 7 Oct 2026 final spec, D67 / D68).
 *
 * One constant per name, in one file, so renames land in one place.
 *   · guest          — not signed in
 *   · in             — signed up, free
 *   · insider        — paying or on the 30-day trial
 *   · curator        — organiser · level of its own
 *   · founding crew  — the first 1,000 insiders (KES 200/month for life)
 *   · plug           — EARNED BADGE for later · NOT a level. Icon only.
 *
 * "went" is NOT a level. It's the "i went" action on one event only.
 *
 * Guest first-load greeting (per spec): "karibu mgeni wetu" (our guest).
 */
export const LEVELS = Object.freeze({
  GUEST: "guest",
  IN: "in",
  INSIDER: "insider",
  CURATOR: "curator",
  FOUNDING_CREW: "founding crew",
});

export const BADGES = Object.freeze({
  PLUG: "plug", // earned, not a level · logic ships later
});

export const GUEST_GREETING = "karibu mgeni wetu";
export const WELCOME_AFTER_SIGNUP = "you're in";

/** Primary (base) level: founding crew > insider > curator > in > guest. */
export function levelOf(user) {
  if (!user) return LEVELS.GUEST;
  const state = (user.membership_state || "free").toLowerCase();
  const insider = state === "trialing" || state === "paid" || state === "founding" || state === "comped";
  if (insider && (state === "founding" || state === "comped" || user.is_founding)) {
    return LEVELS.FOUNDING_CREW;
  }
  if (insider) return LEVELS.INSIDER;
  if (user.is_curator) return LEVELS.CURATOR; // curator-only possible for an unpaid promoter
  return LEVELS.IN;
}

/** All levels a person holds at once (stacking · insider + curator both show). */
export function levelsOf(user) {
  if (!user) return [LEVELS.GUEST];
  const base = levelOf(user);
  const out = [base];
  // Curator stacks with insider or founding crew (shows a second icon).
  if (user.is_curator && base !== LEVELS.CURATOR && base !== LEVELS.GUEST && base !== LEVELS.IN) {
    out.push(LEVELS.CURATOR);
  }
  return out;
}

export function isInsider(user) {
  const l = levelOf(user);
  return l === LEVELS.INSIDER || l === LEVELS.FOUNDING_CREW;
}

/** Celebrator copy used immediately after a brand-new sign-up lands. */
export function labelOf(level) {
  return level || LEVELS.GUEST;
}

export const FOUNDING_BANNER = {
  // The single source of truth for the founding banner copy.
  prefix: "become a founding insider",
  priceLine: "KES 200 a month, for life",
  trialLine: "start free for 30 days",
};

export function foundingBannerLine(remaining) {
  const n = typeof remaining === "number" ? remaining.toLocaleString() : String(remaining ?? "—");
  return `${FOUNDING_BANNER.prefix} · ${FOUNDING_BANNER.priceLine} · ${n} of 1,000 seats left · ${FOUNDING_BANNER.trialLine}`;
}

/** Shared "what insiders unlock" list — the single source for the sheet + membership page. */
export const INSIDER_UNLOCKS = Object.freeze([
  "follow curators · their new events land on your for you",
  "create and join squads · plan your day or night out with your people",
  "mark i went and post about your events",
  "leave reviews and share photos from the event",
  "see insiders' favourites · the events most marked going this fortnight",
  "submit your own events for the desk to review",
]);

/**
 * Vania 7 Oct 2026 · the ONLY features that are free for the "in" level.
 * other benefit belongs to `INSIDER_UNLOCKS`. Guardrail test fails on any
 * user-facing string that mentions "free" alongside paywalled features. // ww-copy-ok: trial-free
 *
 * Rule confirmed by Vania: one-to-one DMs (yap with a person) are free.
 * Squads and squad chats are insider only.
 */
/**
 * Wave C.20 (Vania 7 Oct 2026) · the FOUR surfaces guaranteed free at "in".
 * The free-vs-insider copy guardrail test uses this list to assert the
 * exact strings on the get in page map to "in"-level features. If any of
 * these is ever moved behind insider, the test fails loud.
 */
export const FREE_FEATURES = Object.freeze([
  "know before you go",                          // scores on awarded events
  "every event in nairobi",                      // the full discover / for you feed
  "see which of your people are going",          // attendance signal
  "keep the events you're interested in",        // interested = merged save (W-C.18)
  "message your people one to one",              // 1:1 DMs (yap · W-15.5)
]);

export const FREE_HEADING = "free when you're in";
export const CURATOR_LABEL = Object.freeze({
  one: "curator",
  many: "curators",
  co: "co-curator",
  explainer: "curators are the people behind the events: organisers, promoters and venues.",
});
